import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Zap, ChevronDown, Map as MapIcon } from "lucide-react";
import { api } from "../../lib/api";
import { useTimePrefs, fmtClock } from "../../lib/timePrefs";
import { pairColors } from "../../lib/nhlColors";

const GREY = "#8A94A6";
const ORD = (n) => (n === 1 ? "1st" : n === 2 ? "2nd" : n === 3 ? "3rd" : n === 4 ? "OT" : `${n - 3}OT`);
const pct = (a, b) => (b ? `${Math.round((100 * a) / b)}%` : "—");
const sfx = (n) => ((n % 100 >= 11 && n % 100 <= 13) ? "th" : ["th", "st", "nd", "rd"][n % 10] || "th");
const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
export const stColors = (away, home, colorBy) => pairColors(away, home, colorBy);
// Official primaries; only when both teams share a colour does the away side use its own secondary.
export const stReadable = (away, home, colorBy) => pairColors(away, home, colorBy);

export const useClock = (st) => {
    const { clockMode } = useTimePrefs();
    return (time, period) => fmtClock(String(time || "").padStart(5, "0"), clockMode, (st?.periods || []).find((p) => p.number === period)?.len || 1200);
};

// Half-rink PP shot plot (attacking zone right). NHL coords: x ±100, y ±42.5.
const ShotMap = ({ shots = [], color }) => (
    <svg viewBox="0 0 100 85" className="w-[74px] sm:w-[92px] h-auto shrink-0" data-testid="st-shot-map">
        <rect x="1" y="1" width="98" height="83" rx="14" fill="rgba(0,0,0,0.35)" stroke="rgba(255,255,255,0.16)" />
        <line x1="25" y1="1" x2="25" y2="84" stroke="rgba(59,130,246,0.55)" strokeWidth="1.2" />
        <line x1="89" y1="4" x2="89" y2="81" stroke="rgba(239,68,68,0.5)" strokeWidth="0.8" />
        <path d="M89 36 a6 6 0 0 1 0 13" fill="rgba(59,130,246,0.25)" stroke="rgba(239,68,68,0.5)" strokeWidth="0.6" />
        <circle cx="69" cy="20" r="11" fill="none" stroke="rgba(239,68,68,0.3)" />
        <circle cx="69" cy="65" r="11" fill="none" stroke="rgba(239,68,68,0.3)" />
        {shots.filter((s) => s.x != null && s.y != null).map((s, i) => {
            const x = Math.abs(s.x);
            const y = s.x < 0 ? -s.y : s.y;
            return <circle key={i} cx={x} cy={42.5 - y} r={s.type === "goal" ? 4.2 : 2.8} fill={s.type === "missed-shot" || s.type === "blocked-shot" ? "transparent" : color} stroke="#fff" strokeWidth={s.type === "goal" ? 1.4 : 0.7} />;
        })}
    </svg>
);

// One team's special teams, one compact row: PP + PK badges (stand out), PIM / PP time / True PIM, PP shot map on hover/tap.
export function TeamStrip({ t, tb, logo, c, season }) {
    const [map, setMap] = useState(false);
    const rk = (r) => (r ? `${r.rank}${sfx(r.rank)}` : "");
    const badge = (l, v, p, r, tid) => (
        <span className="inline-flex items-baseline gap-1 rounded-md bg-black/35 border border-white/25 px-1.5 py-0.5 shrink-0" data-testid={tid}>
            <span className="text-[9px] font-black uppercase tracking-wider text-white">{l}</span>
            <b className="text-[14px] font-black text-white tabular-nums leading-none">{v}</b>
            <span className="text-[10px] font-bold text-white/75 tabular-nums">{p}</span>
            {r && <span className="hidden sm:inline text-[9px] font-bold text-white/55">· {r}</span>}
        </span>
    );
    const mini = (l, v) => <span className="shrink-0 text-[10px] text-white/65 tabular-nums whitespace-nowrap">{l} <b className="text-white font-black">{v}</b></span>;
    return (
        <div className="relative flex items-center gap-2 rounded-lg border px-2 py-1.5 min-w-0" style={{ background: `linear-gradient(180deg, ${c}55, ${c}22)`, borderColor: `${c}AA` }} data-testid={`st-strip-${t}`}>
            <span className="flex items-center gap-1 shrink-0">{logo ? <img src={logo} alt={t} className="h-5 w-5 object-contain" /> : null}<b className="text-[11px] font-black text-white">{t}</b></span>
            {badge("PP", `${tb.pp_goals}/${tb.pp_opps}`, pct(tb.pp_goals, tb.pp_opps), rk(season?.pp), `st-pp-${t}`)}
            {badge("PK", `${tb.pk_killed}/${tb.pk_times}`, pct(tb.pk_killed, tb.pk_times), rk(season?.pk), `st-pk-${t}`)}
            <span className="flex items-center gap-2 min-w-0 overflow-hidden">
                {mini("PIM", tb.pim)}
                <span className="hidden sm:inline">{mini("PP time", tb.pp_time)}</span>
                <span className="hidden sm:inline">{mini("True PIM", tb.pim_true)}</span>
            </span>
            <button type="button" onMouseEnter={() => setMap(true)} onMouseLeave={() => setMap(false)} onClick={() => setMap((o) => !o)} title="Power-play shot map" data-testid={`st-map-btn-${t}`}
                className="ml-auto shrink-0 h-6 w-6 rounded-md bg-black/30 border border-white/20 flex items-center justify-center text-white hover:bg-black/50 transition-colors">
                <MapIcon className="h-3.5 w-3.5" />
            </button>
            {map && (
                <div className="absolute right-0 top-full mt-1.5 z-40 rounded-lg border border-white/15 bg-[#0B0D14] p-2 shadow-2xl shadow-black/60" data-testid={`st-map-pop-${t}`}>
                    <div className="text-[9px] font-black uppercase tracking-wider text-white/60 mb-1">{t} power-play shots · {tb.pp_shots.map.length}</div>
                    <div className="w-[180px] [&_svg]:w-full">{<ShotMap shots={tb.pp_shots.map} color={c} />}</div>
                </div>
            )}
        </div>
    );
}

const ptime = (sec) => mmss(Math.max(0, Math.round(sec)));

// What happened in one strength window (shown on hover / tap).
function SegInfo({ s, st, colorBy, clock, per }) {
    const pens = st.penalties.filter((p) => p.affects && !p.coincidental && p.start_abs < s.end && p.end_abs > s.start);
    const goals = st.goals.filter((g) => g.abs >= s.start && g.abs <= s.end && g.abs >= per.offset);
    const shots = s.adv ? st.teams[s.adv].pp_shots.map.filter((x) => x.abs >= s.start && x.abs < s.end) : [];
    const c = s.adv ? colorBy[s.adv] : "#E2E8F0";
    return (
        <div className="w-[300px] max-w-[calc(100vw-24px)] rounded-lg border border-white/15 bg-[#0B0D14] p-2.5 shadow-2xl shadow-black/70 text-[11px] leading-snug" style={{ borderTop: `2px solid ${c}` }} data-testid="st-seg-info">
            <div className="flex flex-wrap items-center gap-x-2 mb-1.5">
                <b className="text-[13px] font-black text-white">{s.label}</b>
                <span className="ml-auto text-white/60 tabular-nums">{ORD(s.start_period)} {clock(s.start_time, s.start_period)} → {s.end_period !== s.start_period ? `${ORD(s.end_period)} ` : ""}{clock(s.end_time, s.end_period)}</span>
            </div>
            <div className="text-white/60 mb-1.5">Length <b className="text-white tabular-nums">{ptime(s.duration)}</b>{s.adv ? <> · PP shots <b className="text-white">{shots.length}</b> ({shots.filter((x) => x.type === "shot-on-goal" || x.type === "goal").length} SOG)</> : null}</div>
            {pens.map((p, i) => (
                <div key={i} className="flex flex-wrap items-center gap-x-1.5 py-1 border-t border-white/[0.08]">
                    <span className="h-1.5 w-1.5 rounded-full shrink-0 bg-[#DC2626]" />
                    <span className="text-white font-semibold">{p.sweater ? `#${p.sweater} ` : ""}{p.player}</span>
                    <span className="text-[#FCA5A5]">{p.infraction}</span>
                    <span className="ml-auto text-white/60">{REASON[p.end_reason] || ""}</span>
                </div>
            ))}
            {goals.map((g, i) => (
                <div key={`g${i}`} className="flex flex-wrap items-center gap-x-1.5 py-1 border-t border-white/[0.08]">
                    <span className="text-[11px] font-black px-1 rounded text-white" style={{ background: colorBy[g.team] }}>{g.strength}</span>
                    <span className="text-white font-semibold">{g.scorer}</span>
                    <span className="ml-auto text-white/60 tabular-nums">{clock(g.time, g.period)} · {g.away_score}-{g.home_score}</span>
                </div>
            ))}
        </div>
    );
}


// One clean strength bar per period (the original design): labelled team-coloured
// blocks, quarter gridlines, slim goal ticks — tap / hover a block for details.
export function PeriodBar({ p, st, colorBy, clock, hov, setHov, noLabel = false }) {
    const inP = (t) => t >= p.offset && t < p.offset + p.len + (p.number === st.periods[st.periods.length - 1].number ? 1 : 0);
    const pos = (t) => `${((t - p.offset) / p.len) * 100}%`;
    const segs = st.segments.filter((s) => s.end > p.offset && s.start < p.offset + p.len);
    const goals = st.goals.filter((g) => inP(g.abs));
    // Penalties called at the very end of the game (nothing served) — a marker at the end of the bar.
    const endPens = st.penalties.filter((x) => x.end_reason === "game_end" && x.end_abs - x.start_abs < 1 && x.start_abs >= p.offset + p.len - 1 && x.start_abs <= p.offset + p.len + 1);
    return (
        <div className="flex items-center gap-3" data-testid={`st-period-${p.number}`}>
            {!noLabel && <span className="w-10 shrink-0 text-[10px] font-black uppercase tracking-widest text-white/45">{ORD(p.number)}</span>}
            <div className="relative flex-1 h-7 rounded-lg bg-white/[0.04] border border-white/10">
                {[0.25, 0.5, 0.75].map((f) => <span key={f} className="absolute top-0 bottom-0 w-px bg-white/[0.08]" style={{ left: `${f * 100}%` }} />)}
                {segs.map((s, i) => {
                    const a = Math.max(s.start, p.offset) - p.offset;
                    const b = Math.min(s.end, p.offset + p.len) - p.offset;
                    const c = s.adv ? (colorBy[s.adv] || GREY) : "#475569";
                    const w = (b - a) / p.len;
                    const k = `${p.number}-${i}`;
                    const txt = w > 0.13 ? s.label : w > 0.06 ? s.label.split(" ").pop() : "";
                    return (
                        <button key={k} type="button" onMouseEnter={() => setHov(k)} onMouseLeave={() => setHov(null)} onClick={() => setHov(hov === k ? null : k)} data-testid={`st-seg-${k}`}
                            className="absolute top-0.5 bottom-0.5 rounded-md border flex items-center justify-center text-[9px] font-black uppercase tracking-wider text-white whitespace-nowrap overflow-hidden transition-[filter]"
                            style={{ left: `${(a / p.len) * 100}%`, width: `${w * 100}%`, background: s.adv ? `linear-gradient(180deg, ${c}55, ${c}22)` : "rgba(255,255,255,0.08)", borderColor: s.adv ? `${c}AA` : "rgba(255,255,255,0.15)", filter: hov === k ? "brightness(1.35)" : "none" }}>
                            {txt}
                        </button>
                    );
                })}
                {goals.map((g, i) => <span key={`g${i}`} title={`${g.strength} goal · ${g.scorer}`} className="absolute -top-1 h-2.5 w-2.5 -translate-x-1/2 rounded-full border border-white pointer-events-none" style={{ left: pos(g.abs), background: colorBy[g.team] || GREY }} data-testid={`st-goal-${p.number}-${i}`} />)}
                {endPens.map((x, i) => {
                    const k = `${p.number}-end-${i}`;
                    return (
                        <React.Fragment key={k}>
                            <button type="button" onMouseEnter={() => setHov(k)} onMouseLeave={() => setHov(null)} onClick={() => setHov(hov === k ? null : k)} data-testid={`st-endpen-${k}`}
                                className="absolute top-0.5 bottom-0.5 w-2 rounded-sm" style={{ right: `${2 + i * 10}px`, background: colorBy[x.team] || GREY }} />
                            {hov === k && (
                                <div className="absolute top-full mt-1.5 right-0 z-30 pointer-events-none">
                                    <div className="w-[300px] max-w-[calc(100vw-24px)] rounded-lg border border-white/15 bg-[#0B0D14] p-2.5 shadow-2xl shadow-black/70 text-[11px] leading-snug" style={{ borderTop: `2px solid #DC2626` }} data-testid="st-endpen-info">
                                        <div className="flex items-center gap-2 mb-1.5"><b className="text-[13px] font-black text-white">{x.team} penalty</b><span className="ml-auto text-white/50 tabular-nums">{ORD(x.period)} {clock(x.called, x.period)}</span></div>
                                        <div className="flex items-center gap-1.5 py-0.5 border-t border-white/[0.06]">
                                            <span className="text-white font-semibold truncate">{x.sweater ? `#${x.sweater} ` : ""}{x.player}</span>
                                            <span className="text-white/60 truncate">{x.infraction} {x.minutes}′</span>
                                        </div>
                                        <div className="text-white/60 mt-1">Called at the final whistle · True PIM <b className="text-white tabular-nums">{x.served}</b></div>
                                    </div>
                                </div>
                            )}
                        </React.Fragment>
                    );
                })}
                {segs.map((s, i) => hov === `${p.number}-${i}` && (
                    <div key={`h${i}`} className="absolute top-full mt-1.5 z-30 pointer-events-none" style={{ left: `min(max(0px, calc(${((Math.max(s.start, p.offset) - p.offset) / p.len) * 100}% - 20px)), calc(100% - 300px))` }}>
                        <SegInfo s={s} st={st} colorBy={colorBy} clock={clock} per={p} />
                    </div>
                ))}
            </div>
        </div>
    );
}

// Shootout: one row per team — team colour = scored, grey = missed, black = not taken yet.
export function SoCircles({ st, colorBy }) {
    const by = { [st.away]: [], [st.home]: [] };
    st.shootout.forEach((x) => (by[x.team] = by[x.team] || []).push(x));
    const rounds = Math.max(3, ...Object.values(by).map((l) => l.length));
    return (
        <div className="flex-1 flex flex-col gap-1">
            {[st.away, st.home].map((t) => (
                <div key={t} className="flex items-center gap-1.5" data-testid={`st-so-${t}`}>
                    <span className="w-8 text-[10px] font-black text-white/60">{t}</span>
                    {Array.from({ length: rounds }, (_, i) => {
                        const x = by[t][i];
                        const bg = !x ? "#000" : x.scored ? (colorBy[t] || GREY) : "#475569";
                        return <span key={i} title={x ? `${x.shooter} — ${x.scored ? "scored" : "missed"}` : "Not taken yet"} className="h-4 w-4 rounded-full border border-white/20" style={{ background: bg }} data-testid={`st-so-${t}-${i + 1}`} />;
                    })}
                </div>
            ))}
        </div>
    );
}

function Timeline({ st, colorBy, clock }) {
    const [hov, setHov] = useState(null);
    return (
        <div className="space-y-3" data-testid="st-timeline">
            {st.periods.map((p) => <PeriodBar key={p.number} p={p} st={st} colorBy={colorBy} clock={clock} hov={hov} setHov={setHov} />)}
            {st.shootout?.length > 0 && (
                <div className="flex items-center gap-3" data-testid="st-period-so">
                    <span className="w-10 shrink-0 text-[10px] font-black uppercase tracking-widest text-white/45">SO</span>
                    <SoCircles st={st} colorBy={colorBy} />
                </div>
            )}
            <div className="flex items-center gap-4 pl-[52px] text-[10px] text-white/50 flex-wrap">
                {[st.away, st.home].map((t) => <span key={t} className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: colorBy[t] || GREY }} />{t} power play</span>)}
                <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-[#475569]" />Even (4v4 / 3v3)</span>
                <span className="inline-flex items-center gap-1.5"><span className="h-3 w-[2px] rounded-full bg-white" />Goal</span>
            </div>
        </div>
    );
}

const REASON = { expired: "Expired", pp_goal: "PP goal", game_end: "Game end", ongoing: "Ongoing", pending: "Queued", coincidental: "Offsetting", non_strength: "—" };

export function Details({ st, colorBy, clock }) {
    const [team, setTeam] = useState(null);
    const players = st.players.filter((r) => !team || r.team === team);
    return (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_220px] gap-3 pt-2" data-testid="st-details">
            <div className="overflow-x-auto">
                <table className="w-full text-[12px] min-w-[620px]">
                    <thead><tr className="text-[12px] font-black uppercase tracking-widest text-white/60 text-left">{["Called", "Player", "Infraction", "Start → End", "True", "Ended", "Situation"].map((h) => <th key={h} className="py-1 pr-2">{h}</th>)}</tr></thead>
                    <tbody>
                        {st.penalties.map((p, i) => (
                            <tr key={i} className="border-t border-white/[0.05]" data-testid={`st-pen-row-${i}`}>
                                <td className="py-1 pr-2 text-white/65 tabular-nums whitespace-nowrap">{ORD(p.period)} {clock(p.called, p.period)}</td>
                                <td className="py-1 pr-2 whitespace-nowrap"><span className="inline-block h-2 w-2 rounded-full mr-1" style={{ background: colorBy[p.team] }} /><b className="text-white/80">{p.team}</b> <span className="text-white font-semibold">{p.player}</span>{p.served_by ? <span className="text-white/60"> ({p.served_by})</span> : null}</td>
                                <td className="py-1 pr-2 text-white/70 whitespace-nowrap">{p.infraction} {p.minutes}′</td>
                                <td className="py-1 pr-2 text-white/50 tabular-nums whitespace-nowrap">{clock(p.start, p.start_period)} → {p.end_period !== p.start_period ? `${ORD(p.end_period)} ` : ""}{clock(p.end, p.end_period)}</td>
                                <td className="py-1 pr-2 font-black text-white tabular-nums">{p.served}</td>
                                <td className="py-1 pr-2 text-white/60 whitespace-nowrap">{REASON[p.end_reason] || "—"}</td>
                                <td className="py-1 pr-2 whitespace-nowrap">{p.segments.length > 1 ? p.segments.map((s, j) => <span key={j} className="mr-1 px-1 rounded bg-white/[0.07] text-white/80 font-bold">{s.label} {mmss(s.secs)}</span>) : <span className="text-white/75 font-bold">{p.situation}</span>}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div data-testid="st-player-pim">
                <div className="flex items-center gap-1 mb-1">
                    <span className="text-[12px] font-bold uppercase tracking-wide text-white/60 mr-auto">PIM · True PIM</span>
                    {[null, st.away, st.home].map((t) => <button key={t || "all"} type="button" onClick={() => setTeam(t)} data-testid={`st-pim-team-${t || "all"}`} className={`px-1.5 py-0.5 rounded text-[12px] font-black uppercase ${team === t ? "bg-white/15 text-white" : "text-white/60 hover:text-white"}`}>{t || "All"}</button>)}
                </div>
                {players.map((r) => (
                    <div key={`${r.team}-${r.player}`} className="flex items-center gap-1.5 text-[12px] py-0.5 border-t border-white/[0.05]">
                        <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: colorBy[r.team] }} />
                        <span className="text-white truncate">{r.player}</span>
                        <span className="ml-auto text-white/60 tabular-nums">{r.pim}:00</span>
                        <b className="text-white tabular-nums w-9 text-right">{r.true}</b>
                    </div>
                ))}
            </div>
        </div>
    );
}

export function NhlSpecialTeams({ gameId, live, logoBy = {}, colorBy: rawColors = {} }) {
    const [open, setOpen] = useState(false);
    const { data: st } = useQuery({
        queryKey: ["nhl-special-teams", gameId],
        queryFn: () => api.get("/nhl/special-teams", { params: { game: gameId } }).then((r) => r.data),
        refetchInterval: live ? 20000 : false, staleTime: 15000,
    });
    const clock = useClock(st);
    if (!st) return null;
    const colorBy = stReadable(st.away, st.home, rawColors);
    const ppGoals = st.goals.filter((g) => g.strength !== "EV");
    return (
        <div className="relative rounded-xl border border-white/10 bg-[#0E0F16] p-3.5" data-testid="nhl-gc-special-teams">
            <div className="relative space-y-2.5">
                <div className="flex items-center gap-2">
                    <Zap className="h-4 w-4 text-white/60" />
                    <h3 className="text-xs font-black uppercase tracking-wider text-white">Special Teams</h3>
                    {ppGoals.length > 0 && <div className="hidden sm:flex items-center gap-1 ml-2 min-w-0 overflow-hidden">
                        {ppGoals.map((g, i) => <span key={i} className="text-[11px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap bg-white/[0.06] text-white/80"><span className="inline-block h-2 w-2 rounded-full mr-1 align-middle" style={{ background: colorBy[g.team] }} />{g.strength} {g.scorer} · {ORD(g.period)} {clock(g.time, g.period)}</span>)}
                    </div>}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {[st.away, st.home].map((t) => <TeamStrip key={t} t={t} tb={st.teams[t]} logo={logoBy[t]} c={colorBy[t]} season={st.season?.[t]} />)}
                </div>
                {st.segments.length ? <Timeline st={st} colorBy={colorBy} clock={clock} /> : <p className="text-[11px] text-white/40">Full strength all game.</p>}
                <button type="button" onClick={() => setOpen((o) => !o)} data-testid="st-details-toggle"
                    className="w-full flex items-center justify-center gap-1 pt-1 text-[11px] font-bold uppercase tracking-wider text-white/60 hover:text-white transition-colors">
                    Penalty details ({st.penalties.length}) <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
                </button>
                {open && <Details st={st} colorBy={colorBy} clock={clock} />}
            </div>
        </div>
    );
}
